import React, { useState, useEffect } from 'react';
import { reportApi, projectApi } from '../services/api';
import { Card, Button, Select, Spinner, Tabs, Badge } from '../components/UI';
import { FilePdf, FileXls, ChartBar, HardHat, CurrencyDollar } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { fmtCurrency, getCurrencySymbol } from '../utils/currencyFormat';
import { useBranding } from '../context/BrandingContext';

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('technical');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [techData, setTechData] = useState(null);
  const [opsData, setOpsData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const branding = useBranding();

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  const params = () => {
    const p = {};
    if (selectedProject) p.project_id = selectedProject;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  };

  const loadReport = async (type) => {
    try {
      if (type === 'technical') { const r = await reportApi.technical(params()); setTechData(r.data); }
      else if (type === 'operational') { const r = await reportApi.operational(params()); setOpsData(r.data); }
      else if (type === 'financial') { const r = await reportApi.financial(params()); setFinData(r.data); }
    } catch (err) { alert('Failed to load report'); }
  };

  useEffect(() => { loadReport(activeTab); }, [activeTab, selectedProject, startDate, endDate]);

  const fmt = (n) => fmtCurrency(n);

  const addPdfBranding = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();
    const devLine = branding.developer_tagline
      ? `Developed by ${branding.developer_name}  |  ${branding.developer_tagline}`
      : `Developed by ${branding.developer_name}`;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageH = doc.internal.pageSize.getHeight();
      const pageW = doc.internal.pageSize.getWidth();
      doc.setDrawColor(200, 200, 200);
      doc.line(14, pageH - 25, pageW - 14, pageH - 25);
      doc.setFontSize(6.5);
      doc.setTextColor(160, 160, 160);
      doc.text('This report is system-generated and subject to verification. The system provider is not liable for decisions made based on this data.', 14, pageH - 20);
      doc.setFontSize(7);
      doc.text(devLine, 14, pageH - 14);
      doc.text(`${branding.company_name}  |  Generated: ${new Date().toLocaleString('en-US')}`, 14, pageH - 9);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 9, { align: 'right' });
    }
  };

  const exportPDF = (title, rows, columns) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, 14, 28);
    if (selectedProject) {
      const pn = projects.find(p => p.id === selectedProject)?.name || '';
      doc.text(`Project: ${pn}`, 14, 34);
    }
    doc.setTextColor(0);
    autoTable(doc, { startY: 40, head: [columns], body: rows, styles: { fontSize: 8 }, margin: { bottom: 32 } });
    addPdfBranding(doc);
    doc.save(`${title.replace(/\s/g, '_')}.pdf`);
  };

  const exportExcel = (title, data, columns) => {
    const ws = XLSX.utils.aoa_to_sheet([columns, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${title.replace(/\s/g, '_')}.xlsx`);
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Weekly Reports</h1>
        <p className="text-sm text-[#666]">Auto-generated technical, operational & financial reports</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm" placeholder="Start" data-testid="start-date" />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
          className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm" placeholder="End" data-testid="end-date" />
      </div>

      <Tabs
        tabs={[
          { id: 'technical', label: 'Technical' },
          { id: 'operational', label: 'Operational' },
          { id: 'financial', label: 'Financial' },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* TECHNICAL */}
      {activeTab === 'technical' && techData && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = techData.milestones.map(m => [m.name, m.planned_date, m.status, m.description || '']);
              exportPDF('Technical Report', rows, ['Milestone', 'Planned Date', 'Status', 'Description']);
            }} data-testid="export-tech-pdf"><FilePdf className="w-4 h-4 mr-1" /> PDF</Button>
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = techData.milestones.map(m => [m.name, m.planned_date, m.status, m.description || '']);
              exportExcel('Technical_Report', rows, ['Milestone', 'Planned Date', 'Status', 'Description']);
            }} data-testid="export-tech-excel"><FileXls className="w-4 h-4 mr-1" /> Excel</Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tech-kpis">
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Total Tasks</p><p className="text-xl font-bold text-[#002FA7]">{techData.tasks.total}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Completed</p><p className="text-xl font-bold text-[#00C853]">{techData.tasks.completed}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">In Progress</p><p className="text-xl font-bold text-[#FFAB00]">{techData.tasks.in_progress}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Delayed</p><p className="text-xl font-bold text-[#FF3333]">{techData.tasks.delayed}</p></Card>
          </div>

          {techData.milestones.length > 0 && (
            <Card className="p-4" data-testid="milestone-report">
              <h3 className="text-sm font-semibold mb-2">Milestones</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="pb-2">Name</th><th className="pb-2">Planned Date</th><th className="pb-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {techData.milestones.map((m, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0]">
                        <td className="py-2 font-medium">{m.name}</td>
                        <td className="py-2 text-[#666]">{m.planned_date ? new Date(m.planned_date).toLocaleDateString() : '-'}</td>
                        <td className="py-2"><Badge variant={m.status === 'COMPLETED' ? 'success' : m.status === 'IN_PROGRESS' ? 'warning' : 'default'}>{m.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* OPERATIONAL */}
      {activeTab === 'operational' && opsData && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = Object.entries(opsData.manpower.by_category).map(([c, v]) => [c, v.workers, v.hours]);
              exportPDF('Operational Report', rows, ['Category', 'Workers', 'Hours']);
            }} data-testid="export-ops-pdf"><FilePdf className="w-4 h-4 mr-1" /> PDF</Button>
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = Object.entries(opsData.manpower.by_category).map(([c, v]) => [c, v.workers, v.hours]);
              exportExcel('Operational_Report', rows, ['Category', 'Workers', 'Hours']);
            }} data-testid="export-ops-excel"><FileXls className="w-4 h-4 mr-1" /> Excel</Button>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid="ops-kpis">
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Total Workers</p><p className="text-xl font-bold text-[#002FA7]">{opsData.manpower.total_worker_entries}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Man-Hours</p><p className="text-xl font-bold text-[#0A0A0A]">{opsData.manpower.total_man_hours}</p></Card>
          </div>

          {Object.keys(opsData.manpower.by_category).length > 0 && (
            <Card className="p-4" data-testid="manpower-report">
              <h3 className="text-sm font-semibold mb-2">Manpower by Category</h3>
              <div className="space-y-2">
                {Object.entries(opsData.manpower.by_category).map(([cat, val]) => (
                  <div key={cat} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{cat}</span>
                    <div className="flex gap-4 text-sm"><span>{val.workers} workers</span><span className="font-bold">{val.hours} hrs</span></div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {Object.keys(opsData.materials.by_material).length > 0 && (
            <Card className="p-4" data-testid="material-report">
              <h3 className="text-sm font-semibold mb-2">Material Usage</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b">
                    <th className="pb-2">Material</th><th className="pb-2 text-right">Received</th><th className="pb-2 text-right">Used</th><th className="pb-2 text-right">Balance</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(opsData.materials.by_material).map(([name, val]) => (
                      <tr key={name} className="border-b border-[#F0F0F0]">
                        <td className="py-2">{name}</td>
                        <td className="py-2 text-right">{val.received}</td>
                        <td className="py-2 text-right">{val.used}</td>
                        <td className="py-2 text-right font-bold">{val.received - val.used}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* FINANCIAL */}
      {activeTab === 'financial' && finData && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = Object.entries(finData.costs.by_type).map(([t, v]) => [t, v.count, fmt(v.total)]);
              rows.push(['TOTAL COST', '', fmt(finData.costs.total)]);
              rows.push(['Revenue', finData.revenue.invoice_count, fmt(finData.revenue.total_revenue)]);
              rows.push(['Profit/Loss', '', fmt(finData.profit_loss)]);
              exportPDF('Financial Report', rows, ['Item', 'Count', 'Amount']);
            }} data-testid="export-fin-pdf"><FilePdf className="w-4 h-4 mr-1" /> PDF</Button>
            <Button size="sm" variant="secondary" onClick={() => {
              const rows = Object.entries(finData.costs.by_type).map(([t, v]) => [t, v.count, v.total]);
              rows.push(['Total Cost', '', finData.costs.total]);
              rows.push(['Revenue', finData.revenue.invoice_count, finData.revenue.total_revenue]);
              rows.push(['Profit/Loss', '', finData.profit_loss]);
              exportExcel('Financial_Report', rows, ['Item', 'Count', 'Amount']);
            }} data-testid="export-fin-excel"><FileXls className="w-4 h-4 mr-1" /> Excel</Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fin-kpis">
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Total Cost</p><p className="text-xl font-bold text-[#FF3333]">{fmt(finData.costs.total)}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Revenue</p><p className="text-xl font-bold text-[#00C853]">{fmt(finData.revenue.total_revenue)}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Profit/Loss</p><p className={`text-xl font-bold ${finData.profit_loss >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>{fmt(finData.profit_loss)}</p></Card>
            <Card className="p-3 text-center"><p className="text-[10px] text-[#999] uppercase">Outstanding</p><p className="text-xl font-bold text-[#FFAB00]">{fmt(finData.outstanding)}</p></Card>
          </div>

          {Object.keys(finData.costs.by_type).length > 0 && (
            <Card className="p-4" data-testid="cost-type-report">
              <h3 className="text-sm font-semibold mb-2">Costs by Type</h3>
              {Object.entries(finData.costs.by_type).map(([t, v]) => (
                <div key={t} className="flex items-center justify-between py-1 border-b border-[#F0F0F0]">
                  <span className="text-sm capitalize">{t}</span>
                  <div className="flex gap-4 text-sm"><span className="text-[#999]">{v.count} entries</span><span className="font-bold">{fmt(v.total)}</span></div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
